import { storageManager } from "./storageManager";

const EXPORT_PROGRESS_KEY_PREFIX = "vidmuse_thread";

export interface ThreadExportProgressSnapshot {
  id: string;
  createdTime: number;
  dismissed: boolean;
  downloaded: boolean;
}

type LegacySnapshot = ThreadExportProgressSnapshot & { threadId?: string };

export interface ThreadArtifactNavigatorSnapshot {
  showStudioHint?: boolean;
}

export type TimelinePlaybackMode = "smooth" | "quality";
export const DEFAULT_PLAYBACK_MODE: TimelinePlaybackMode = "smooth";

const getExportProgressKey = (threadId: string) =>
  `${EXPORT_PROGRESS_KEY_PREFIX}::${threadId}::export_progress`;
const getArtifactNavigatorKey = (threadId: string) =>
  `${EXPORT_PROGRESS_KEY_PREFIX}::${threadId}::artifact_navigator`;
const getPlaybackModeKey = (threadId: string) =>
  `${EXPORT_PROGRESS_KEY_PREFIX}::${threadId}::timeline_playback_mode`;

export async function loadThreadExportProgress(
  threadId: string
): Promise<ThreadExportProgressSnapshot> {
  const snapshot = await storageManager.get<LegacySnapshot>(
    getExportProgressKey(threadId)
  );
  if (!snapshot) {
    console.warn("No export progress found for thread");
    return {
      id: "",
      createdTime: 0,
      dismissed: false,
      downloaded: false,
    };
  }

  return snapshot;
}

export async function persistThreadExportProgress(
  threadId: string,
  snapshot: ThreadExportProgressSnapshot | null
) {
  if (!snapshot) {
    await storageManager.remove(getExportProgressKey(threadId));
    return;
  }
  await storageManager.set(getExportProgressKey(threadId), snapshot);
}

export async function sweepExportProgress() {
  await storageManager.sweep(EXPORT_PROGRESS_KEY_PREFIX);
}

export async function loadThreadArtifactNavigatorState(
  threadId: string
): Promise<ThreadArtifactNavigatorSnapshot> {
  const snapshot = await storageManager.get<ThreadArtifactNavigatorSnapshot>(
    getArtifactNavigatorKey(threadId)
  );
  return snapshot ?? {};
}

export async function persistThreadArtifactNavigatorState(
  threadId: string,
  snapshot: ThreadArtifactNavigatorSnapshot
) {
  await storageManager.set(getArtifactNavigatorKey(threadId), snapshot);
}

export async function loadThreadPlaybackMode(
  threadId: string
): Promise<TimelinePlaybackMode> {
  const stored = await storageManager.get<TimelinePlaybackMode>(
    getPlaybackModeKey(threadId)
  );
  return stored ?? DEFAULT_PLAYBACK_MODE;
}

export async function persistThreadPlaybackMode(
  threadId: string,
  mode: TimelinePlaybackMode
) {
  await storageManager.set(getPlaybackModeKey(threadId), mode);
}
